import React from 'react';
import {
  Box,
  Table,
  Header,
  SpaceBetween,
  Button,
  StatusIndicator,
  Container,
  Alert,
  Popover
} from '@amzn/awsui-components-console';
import StatusBadge from './StatusBadge';
import './AccessStatusTable.scss';

export interface AccessTableInfo {
  name: string;
  status: 'granted' | 'denied' | 'pending' | 'not_onboarded' | 'role_no_access';
  checks?: {
    userCanRead?: boolean;
    addaptRoleHasAccess?: boolean;
    addaptIsSubscribed?: boolean;
  };
  issues?: string[];
  action?: {
    type: 'request' | 'deeplink';
    label: string;
    url?: string;
    onClick?: () => void;
  };
}

export interface AccessSystemGroup {
  system_name: string;
  tables: AccessTableInfo[];
}

export interface AccessStatusTableProps {
  groups: AccessSystemGroup[];
  summary?: string;
  loading?: boolean;
  degraded?: boolean;
  degraded_warning?: string;
}

/**
 * AccessStatusTable - Shows per-table access status grouped by governance system
 * 
 * Displays access check results with:
 * - Tables grouped by system (FDV/Fintella, Andes, etc.)
 * - Status indicators (granted/denied/pending)
 * - Inline actions (request access, deep links)
 * - Summary of accessible tables
 * - Color-coded headers based on overall status
 */
export const AccessStatusTable: React.FC<AccessStatusTableProps> = ({
  groups,
  summary,
  loading = false,
  degraded = false,
  degraded_warning
}) => {
  // Calculate overall status for header styling
  const allTables = groups.reduce((acc: AccessTableInfo[], g: AccessSystemGroup) => {
    return acc.concat(g.tables);
  }, []);
  const grantedCount = allTables.filter((t: AccessTableInfo) => t.status === 'granted').length;
  const totalCount = allTables.length;
  
  let headerVariant: 'success' | 'warning' | 'error' | 'info' = 'info';
  if (grantedCount === totalCount && totalCount > 0) {
    headerVariant = 'success'; // All granted
  } else if (grantedCount === 0 && totalCount > 0) {
    headerVariant = 'error'; // All denied
  } else if (grantedCount > 0 && grantedCount < totalCount) {
    headerVariant = 'warning'; // Some denied
  }

  const getStatusIndicator = (status: AccessTableInfo['status']) => {
    switch (status) {
      case 'granted':
        return <StatusBadge variant="approved" label="Granted" />;
      case 'denied':
        return <StatusBadge variant="denied" />;
      case 'pending':
        return <StatusBadge variant="pending" />;
      case 'not_onboarded':
        return <StatusBadge variant="not_onboarded" label="Not Onboarded" />;
      case 'role_no_access':
        return <StatusBadge variant="role_no_access" label="Role No Access" />;
      default:
        return <StatusBadge variant="invalid" label="Unknown" />;
    }
  };

  const renderChecksBreakdown = (item: AccessTableInfo) => {
    if (!item.checks) return null;
    const checkLabels: { key: keyof NonNullable<AccessTableInfo['checks']>; label: string }[] = [
      { key: 'userCanRead', label: 'User Can Read' },
      { key: 'addaptRoleHasAccess', label: 'ADDAPT Role Access' },
      { key: 'addaptIsSubscribed', label: 'Subscribed in Glue' },
    ];
    return (
      <Popover
        dismissButton={false}
        position="top"
        size="medium"
        triggerType="text"
        content={
          <SpaceBetween size="xxs">
            {checkLabels.map(({ key, label }) => {
              const val = item.checks?.[key];
              return (
                <Box key={key} fontSize="body-s">
                  <StatusIndicator type={val ? 'success' : 'error'}>
                    {label}
                  </StatusIndicator>
                </Box>
              );
            })}
            {item.issues && item.issues.length > 0 && (
              <Box fontSize="body-s" color="text-status-error">
                Issues: {item.issues.join(', ')}
              </Box>
            )}
          </SpaceBetween>
        }
      >
        <Box fontSize="body-s" color="text-body-secondary" variant="span">
          View checks
        </Box>
      </Popover>
    );
  };

  const renderAction = (table: AccessTableInfo) => {
    if (!table.action) return null;

    if (table.action.type === 'request') {
      return (
        <Button
          variant="link"
          iconAlign="right"
          iconName="external"
          onClick={table.action.onClick}
        >
          {table.action.label}
        </Button>
      );
    }

    if (table.action.type === 'deeplink' && table.action.url) {
      return (
        <Button
          variant="link"
          iconAlign="right"
          iconName="external"
          href={table.action.url}
          target="_blank"
        >
          {table.action.label}
        </Button>
      );
    }

    return null;
  };

  // Show loading skeleton
  if (loading) {
    return (
      <Container
        header={
          <Header variant="h3">
            Checking table access...
          </Header>
        }
      >
        <Box padding={{ vertical: 'l' }}>
          <StatusIndicator type="loading">
            Verifying access permissions...
          </StatusIndicator>
        </Box>
      </Container>
    );
  }

  return (
    <div className="access-status-table">
      <SpaceBetween size="m">
        {degraded && (
          <Alert type="warning" header="Degraded Results">
            {degraded_warning || 'Some backend checks (Glue catalog or ADDAPT role) failed. Results may be incomplete — access was denied as a precaution.'}
          </Alert>
        )}
        {groups.map((group, groupIndex) => {
          const hasActions = group.tables.some(t => t.action);
          
          return (
            <Container
              key={groupIndex}
              header={
                <Header
                  variant="h3"
                  description={`${group.tables.length} table${group.tables.length !== 1 ? 's' : ''}`}
                >
                  {group.system_name}
                </Header>
              }
            >
              <Table
                columnDefinitions={[
                  {
                    id: 'table',
                    header: 'Table',
                    cell: (item: AccessTableInfo) => (
                      <code style={{ fontSize: '13px' }}>
                        {item.name}
                      </code>
                    ),
                    width: '50%',
                    minWidth: 200
                  },
                  {
                    id: 'status',
                    header: 'Status',
                    cell: (item: AccessTableInfo) => (
                      <SpaceBetween size="xxxs" direction="vertical">
                        {getStatusIndicator(item.status)}
                        {renderChecksBreakdown(item)}
                      </SpaceBetween>
                    ),
                    width: hasActions ? '25%' : '50%',
                    minWidth: 120
                  },
                  ...(hasActions ? [{
                    id: 'action',
                    header: 'Action',
                    cell: (item: AccessTableInfo) => renderAction(item),
                    width: '25%',
                    minWidth: 150
                  }] : [])
                ]}
                items={group.tables}
                variant="embedded"
                empty={
                  <Box textAlign="center" color="inherit">
                    <b>No tables</b>
                    <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                      No tables found in this system.
                    </Box>
                  </Box>
                }
              />
            </Container>
          );
        })}

        {summary && (
          <Box
            textAlign="center"
            padding={{ vertical: 's' }}
            fontSize="body-m"
            fontWeight="bold"
            color={
              headerVariant === 'success' ? 'text-status-success' :
              headerVariant === 'error' ? 'text-status-error' :
              headerVariant === 'warning' ? 'text-status-warning' :
              'text-body-secondary'
            }
          >
            {summary}
          </Box>
        )}
      </SpaceBetween>
    </div>
  );
};

export default AccessStatusTable;
